import * as React from "react";
import { InboxOutlined } from "@ant-design/icons";
import { Form, Upload, Typography, UploadProps, UploadFile, notification } from "antd";
import { RcFile } from "types";

const { Dragger } = Upload;
const { Paragraph } = Typography;

interface FileUploadProps extends UploadProps {
  onRemove?: UploadProps["onRemove"];
  onUploadSuccess?: (file: RcFile) => void;
  required?: boolean;
  multiple?: boolean;
  name?: string;
  label?: string | React.ReactNode;
  getValueProps?: (v: any) => { value: any };
  maxCount?: number;
  formItemClassName?: string;
}

const ALLOWED_FORMATS = ".pdf, .doc, .docx, .jpg, .jpeg, .png, .msword";

const addThumbURL = (file: UploadFile) => {
  // new Chrome update prevents opening files via base64
  file.thumbUrl = URL.createObjectURL(file.originFileObj as File);
  return file;
};

const FileUpload: React.FC<FileUploadProps> = ({
  onRemove,
  required = false,
  multiple = false,
  name,
  label,
  getValueProps,
  maxCount,
  children,
  formItemClassName,
  className,
  onUploadSuccess,
  ...rest
}) => {
  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e.map(addThumbURL);
    }
    return e && e.fileList.map(addThumbURL);
  };

  const dragger = (
    <Dragger
      {...rest}
      className={className}
      multiple={multiple}
      accept={ALLOWED_FORMATS}
      onRemove={onRemove}
      maxCount={maxCount}
      customRequest={({ onSuccess, file }) => {
        onSuccess?.(file);

        onUploadSuccess?.(file as RcFile);
      }}
      isImageUrl={(file) =>
        ["jpg", "jpeg", "png"].some(
          (format) => file.type?.indexOf(format) !== -1
        )
      }
      beforeUpload={(file) => {
        const format = file.type.split("/")[1];

        if (file.size / 1024 / 1024 > 5) {
          notification.error({
            message: 'File is too big'
          })

          return Upload.LIST_IGNORE;
        }

        if (!/(pdf|doc|docx|jpg|jpeg|png|msword)$/g.test(format)) {
          notification.error({
            message: 'Invalid file format',
            description: `Supported formats ${ALLOWED_FORMATS}`
          })

          return Upload.LIST_IGNORE;
        }
      }}
    >
      {children || (
        <>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <Paragraph>
            Click or drag file to this area to upload
          </Paragraph>
          {multiple && (
            <Paragraph>
              Multiple files can be added to this Record
            </Paragraph>
          )}
        </>
      )}
    </Dragger>
  );

  if (!name) {
    return dragger;
  }

  return (
    <Form.Item
      name={name}
      label={label}
      className={formItemClassName}
      getValueProps={getValueProps}
      getValueFromEvent={normFile}
      valuePropName="fileList"
      rules={[
        {
          required,
          message: "Can't be blank",
        },
      ]}
    >
      {dragger}
    </Form.Item>
  );
};

export default FileUpload;
